import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["sidebar", "overlay", "submenu", "chevron"]

  connect() {
    this.isExpanded = localStorage.getItem("sidebarExpanded") !== "false"
    this.isMobileOpen = false

    // Set initial state based on viewport
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.handleResize)

    // Close sidebar on Turbo navigation (mobile)
    this.handleNavigation = this.closeMobile.bind(this)
    document.addEventListener("turbo:before-visit", this.handleNavigation)

    this.updateState()
    this.autoExpandActiveSections()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    document.removeEventListener("turbo:before-visit", this.handleNavigation)
  }

  toggle() {
    if (this.isMobile()) {
      this.toggleMobile()
    } else {
      this.isExpanded = !this.isExpanded
      localStorage.setItem("sidebarExpanded", this.isExpanded)
      this.updateState()
    }
  }

  toggleMobile() {
    this.isMobileOpen = !this.isMobileOpen
    this.updateMobileState()
  }

  closeMobile() {
    if (this.isMobileOpen) {
      this.isMobileOpen = false
      this.updateMobileState()
    }
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.closeMobile()
    }
  }

  handleResize() {
    if (!this.isMobile() && this.isMobileOpen) {
      // Close mobile overlay when switching to desktop
      this.isMobileOpen = false
      this.updateMobileState()
    }
    this.updateState()
  }

  isMobile() {
    return window.innerWidth < 1024 // lg breakpoint
  }

  updateState() {
    if (this.isMobile()) {
      // On mobile, sidebar width is always w-72, controlled by translate
      this.sidebarTarget.classList.remove("w-20")
      this.sidebarTarget.classList.add("w-72")
    } else {
      // On desktop, toggle between expanded and collapsed
      if (this.isExpanded) {
        this.sidebarTarget.classList.remove("w-20")
        this.sidebarTarget.classList.add("w-72")
      } else {
        this.sidebarTarget.classList.remove("w-72")
        this.sidebarTarget.classList.add("w-20")
      }
    }
  }

  updateMobileState() {
    if (this.isMobileOpen) {
      this.sidebarTarget.classList.remove("-translate-x-full")
      if (this.hasOverlayTarget) {
        this.overlayTarget.classList.remove("hidden")
      }
    } else {
      this.sidebarTarget.classList.add("-translate-x-full")
      if (this.hasOverlayTarget) {
        this.overlayTarget.classList.add("hidden")
      }
    }
  }

  mouseEnter() {
    if (!this.isExpanded && !this.isMobile()) {
      this.sidebarTarget.classList.add("hover:w-72")
    }
  }

  mouseLeave() {
    if (!this.isExpanded && !this.isMobile()) {
      this.sidebarTarget.classList.remove("hover:w-72")
    }
  }

  toggleSubmenu(event) {
    event.preventDefault()
    const button = event.currentTarget

    // Find the submenu that follows this button
    const submenu = button.nextElementSibling
    const chevron = button.querySelector('svg[data-sidebar-target="chevron"]')

    if (submenu) {
      const isHidden = submenu.classList.contains("hidden")

      if (isHidden) {
        submenu.classList.remove("hidden")
        if (chevron) {
          chevron.style.transform = "rotate(180deg)"
        }
      } else {
        submenu.classList.add("hidden")
        if (chevron) {
          chevron.style.transform = "rotate(0deg)"
        }
      }
    }
  }

  autoExpandActiveSections() {
    // Find all buttons with data-auto-expand="true" and expand their submenus
    const activeButtons = this.element.querySelectorAll(
      'button[data-auto-expand="true"]'
    )

    activeButtons.forEach((button) => {
      const submenu = button.nextElementSibling
      const chevron = button.querySelector(
        'svg[data-sidebar-target="chevron"]'
      )

      if (submenu && submenu.classList.contains("hidden")) {
        submenu.classList.remove("hidden")
        if (chevron) {
          chevron.style.transform = "rotate(180deg)"
        }
      }
    })
  }
}
